// components/publisher/PublisherInsightsContent.jsx
import React from 'react';
import { 
    LineChart, Line, BarChart, Bar, PieChart, Pie, 
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Cell
} from 'recharts';
import { Building2, TrendingUp, Gamepad2, Trophy } from 'lucide-react';

export default function PublisherInsightsContent({ insights }) {
    const formatRevenue = (value) => {
        if (value >= 1000000000) {
            return `$${(value / 1000000000).toFixed(1)}B`;
        }
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`;
        }
        return `$${(value / 1000).toFixed(1)}K`;
    };

    const { 
        summary, 
        classDistribution, 
        revenueAnalysis,
        developmentApproach,
        genreDistribution,
        releaseStrategy,
        portfolioAnalysis
    } = insights;

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Publisher Insights</h2>
                <div className="text-sm text-gray-500">
                    Last updated: {new Date().toLocaleDateString()}
                </div>
            </div>

            {/* Compact Top Stats Row */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-gray-600">Publishers</span>
                    </div>
                    <div className="text-lg font-bold">{summary.totalPublishers.toLocaleString()}</div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-gray-600">Total Revenue</span>
                    </div>
                    <div className="text-lg font-bold">{formatRevenue(summary.totalRevenue)}</div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Gamepad2 className="w-4 h-4 text-purple-500" />
                        <span className="text-xs text-gray-600">Total Games</span>
                    </div>
                    <div className="text-lg font-bold">{summary.totalGames.toLocaleString()}</div>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-4 h-4 text-orange-500" />
                        <span className="text-xs text-gray-600">Median Revenue</span>
                    </div>
                    <div className="text-lg font-bold">{formatRevenue(summary.medianRevenue)}</div>
                </div>
            </div>

            {/* Three Column Layout for Pie Charts */}
            <div className="grid grid-cols-3 gap-4">
                {/* Publisher Class Distribution */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium mb-2">Publisher Classes</h3>
                    <div className="h-48">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={classDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={60}
                                >
                                    {classDistribution.map((entry, index) => (
                                        <Cell key={index} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Development Approach */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium mb-2">Development Approach</h3>
                    <div className="h-48">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={developmentApproach}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={60}
                                >
                                    <Cell fill="#3B82F6" />
                                    <Cell fill="#F59E0B" />
                                </Pie>
                                <Tooltip />
                                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Release Strategy */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium mb-2">Release Strategy</h3>
                    <div className="h-48">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={releaseStrategy}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={60}
                                >
                                    <Cell fill="#6366F1" />
                                    <Cell fill="#10B981" />
                                </Pie>
                                <Tooltip />
                                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Revenue Analysis */}
            <div className="grid grid-cols-2 gap-4">
                {/* Revenue by Publisher Class */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium mb-2">Revenue by Publisher Class</h3>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={Object.values(revenueAnalysis)}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="class" />
                                <YAxis tickFormatter={formatRevenue} />
                                <Tooltip formatter={formatRevenue} />
                                <Bar dataKey="totalRevenue" fill="#3B82F6" name="Total Revenue" />
                                <Bar dataKey="avgRevenue" fill="#10B981" name="Average Revenue" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Portfolio Size Impact */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium mb-2">Portfolio Size Impact</h3>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <LineChart data={portfolioAnalysis}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="size" />
                                <YAxis tickFormatter={formatRevenue} />
                                <Tooltip formatter={formatRevenue} />
                                <Line 
                                    type="monotone" 
                                    dataKey="medianRevenue" 
                                    stroke="#3B82F6" 
                                    name="Median Revenue"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Genre Analysis */}
            <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium mb-2">Genre Distribution by Publisher Class</h3>
                <div className="h-64">
                    <ResponsiveContainer>
                        <BarChart data={genreDistribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="genre" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="AAA" stackId="a" fill="#3B82F6" name="AAA" />
                            <Bar dataKey="AA" stackId="a" fill="#10B981" name="AA" />
                            <Bar dataKey="Indie" stackId="a" fill="#6366F1" name="Indie" />
                            <Bar dataKey="Hobbyist" stackId="a" fill="#F59E0B" name="Hobbyist" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}